import React, { useState, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { MinusCircle, Star } from 'lucide-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { SimulationSettingsPanel } from '../../shared';
import {
  recalculateSpreadSimulation,
  formatCurrency,
} from '../../../utils/spreadCalculations';
import { formatStrikePair } from '../../../utils/spreadMappings';

/**
 * Column groups. "Legs" exposes the two individual contracts behind each
 * spread — the thing a spread table has that a single-contract table doesn't,
 * and what a user checks before actually placing the order.
 */
const COLUMN_GROUPS = {
  Simulation: [
    'Underlying Price', 'Simulated Underlying', 'Underlying Gain %',
    'Days to Gain', 'Simulated Spread Value', 'Simulated P&L',
    'Return on Risk %', 'Spread Value Gain %',
    'Ann. Return (Target)', 'Ann. Return (Expiry)',
  ],
  'Spread Details': [
    'Direction', 'Entry Type', 'Width', 'Max Profit', 'Max Loss', 'Risk',
    'Breakeven', 'Days Until Expiration', 'Exp Label',
  ],
  Legs: [
    'Long Strike', 'Long Premium', 'Long IV', 'Long Delta',
    'Short Strike', 'Short Premium', 'Short IV', 'Short Delta',
    'Net Delta', 'Net Theta', 'Net Vega',
  ],
  All: [
    'Direction', 'Entry Type', 'Width', 'Underlying Price', 'Simulated Underlying',
    'Underlying Gain %', 'Days to Gain', 'Simulated Spread Value', 'Simulated P&L',
    'Return on Risk %', 'Spread Value Gain %', 'Ann. Return (Target)',
    'Ann. Return (Expiry)', 'Max Profit', 'Max Loss', 'Risk', 'Breakeven',
    'Days Until Expiration', 'Exp Label', 'Long Strike', 'Long Premium', 'Long IV',
    'Long Delta', 'Short Strike', 'Short Premium', 'Short IV', 'Short Delta',
    'Net Delta', 'Net Theta', 'Net Vega',
  ],
};

const STICKY_COLUMNS = ['Ticker', 'Spread Type', 'Strikes', 'Expiration', 'Net Entry'];

const COLUMN_TOOLTIPS = {
  'Net Entry': "What the spread costs (debit) or pays you (credit), per share.",
  Width: 'Distance between the two strikes — the spread\'s total span.',
  'Max Profit': 'The most this spread can make per contract. Verticals cap both sides.',
  'Max Loss': 'The most this spread can lose per contract.',
  Risk: 'Capital at risk — the denominator for Return on Risk.',
  Breakeven: 'Underlying price where the position breaks even at expiration.',
  'Return on Risk %': 'Projected P&L divided by max loss. The comparable number across debit and credit spreads.',
  'Spread Value Gain %': "Percentage change in the spread's own value, signed from your position's perspective.",
  'Simulated Spread Value': 'What the spread is projected to be worth at the exit date.',
  'Simulated P&L': 'Projected profit or loss in dollars, per contract.',
  'Ann. Return (Target)': 'Return on risk annualized over the holding period.',
  'Ann. Return (Expiry)': 'Return on risk annualized over the full time to expiration.',
  'Long Premium': 'Market premium of the leg you buy.',
  'Short Premium': 'Market premium of the leg you sell.',
  'Net Delta': 'Long-leg delta minus short-leg delta — the spread\'s directional exposure.',
  'Net Theta': 'Net daily time decay. Positive favors credit spreads.',
  'Net Vega': 'Net sensitivity to implied volatility.',
};

const rowKey = (row) => [
  row['Ticker'], row['Spread Key'], row['Long Strike'],
  row['Short Strike'], row['Expiration'],
].join('|');

const CURRENCY_COLUMNS = new Set([
  'Underlying Price', 'Simulated Underlying', 'Net Entry', 'Max Profit', 'Max Loss',
  'Risk', 'Breakeven', 'Simulated Spread Value', 'Simulated P&L', 'Width',
  'Long Strike', 'Short Strike', 'Long Premium', 'Short Premium',
]);
const PERCENT_COLUMNS = new Set([
  'Return on Risk %', 'Spread Value Gain %', 'Underlying Gain %',
  'Ann. Return (Target)', 'Ann. Return (Expiry)', 'Long IV', 'Short IV',
]);

/**
 * Detail table for spread results — the per-row companion to the heatmap.
 * Shows one row per spread with a full metric breakdown, re-priced locally by
 * the shared SimulationSettingsPanel.
 */
function SpreadDetailTable({
  results,
  favoritedRows, setFavoritedRows,
  removedRows, setRemovedRows,
  showFavoritesOnly, setShowFavoritesOnly,
  filters,
  defaultDays,
}) {
  const [selectedGroup, setSelectedGroup] = useState('Simulation');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [simulationActive, setSimulationActive] = useState(false);
  const initialDays = defaultDays || 30;
  const [daysToTarget, setDaysToTarget] = useState(initialDays);
  const [underlyingGain, setUnderlyingGain] = useState(0);

  // Flatten the per-leg payload so leg fields sort and export like any other
  // column, instead of every consumer reaching into _legs.
  const flattened = useMemo(() => (results || []).map((row) => {
    const legs = row._legs || {};
    const long = legs.long || {};
    const short = legs.short || {};
    return {
      ...row,
      'Long Premium': long.premium,
      'Long IV': long.iv != null ? long.iv * 100 : null,
      'Long Delta': long.delta,
      'Short Premium': short.premium,
      'Short IV': short.iv != null ? short.iv * 100 : null,
      'Short Delta': short.delta,
    };
  }), [results]);

  const processedData = useMemo(() => {
    if (!simulationActive) return flattened;
    return flattened.map((row) => ({
      ...row,
      ...recalculateSpreadSimulation(row, daysToTarget, underlyingGain),
    }));
  }, [flattened, simulationActive, daysToTarget, underlyingGain]);

  const filteredData = useMemo(() => {
    const { tickers = [], expirations = [], spreadTypes = [] } = filters || {};
    return processedData.filter((row) => {
      const key = rowKey(row);
      return (
        (tickers.length === 0 || tickers.includes(row['Ticker']))
        && (expirations.length === 0 || expirations.includes(row['Expiration']))
        && (spreadTypes.length === 0 || spreadTypes.includes(row['Spread Type']))
        && !removedRows.has(key)
        && (!showFavoritesOnly || favoritedRows.has(key))
      );
    });
  }, [processedData, filters, removedRows, showFavoritesOnly, favoritedRows]);

  const sortedData = useMemo(() => {
    const sortable = [...filteredData];
    sortable.sort((a, b) => {
      const aFav = favoritedRows.has(rowKey(a)) ? 0 : 1;
      const bFav = favoritedRows.has(rowKey(b)) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      if (!sortConfig.key) {
        const t = String(a['Ticker'] || '').localeCompare(String(b['Ticker'] || ''));
        if (t !== 0) return t;
        return String(a['Expiration'] || '').localeCompare(String(b['Expiration'] || ''));
      }
      const aVal = sortConfig.key === 'Strikes' ? parseFloat(a['Long Strike']) : a[sortConfig.key];
      const bVal = sortConfig.key === 'Strikes' ? parseFloat(b['Long Strike']) : b[sortConfig.key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      const cmp = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
    return sortable;
  }, [filteredData, sortConfig, favoritedRows]);

  const allColumns = useMemo(
    () => [...STICKY_COLUMNS, ...(COLUMN_GROUPS[selectedGroup] || [])],
    [selectedGroup]
  );

  // Slider ceiling: never let the hold run past the soonest expiration on screen.
  const maxDays = useMemo(() => {
    const values = (results || [])
      .map((r) => parseFloat(r['Days Until Expiration']))
      .filter((d) => d > 0);
    return values.length ? Math.min(...values) : 365;
  }, [results]);

  const handleSort = (column) => {
    setSortConfig((prev) => (
      prev.key === column
        ? { key: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key: column, direction: 'asc' }
    ));
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return <FaSort className="inline ml-1 opacity-50" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="inline ml-1" />
      : <FaSortDown className="inline ml-1" />;
  };

  const toggleFavorite = (row) => {
    const key = rowKey(row);
    setFavoritedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const removeRow = (row) => {
    const key = rowKey(row);
    setRemovedRows((prev) => new Set([...prev, key]));
    setFavoritedRows((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const formatValue = (row, col) => {
    if (col === 'Strikes') return formatStrikePair(row['Long Strike'], row['Short Strike']);
    if (col === 'Net Entry') {
      return `${formatCurrency(row['Net Entry'])} ${row['Entry Type'] === 'Credit' ? 'Cr' : 'Dr'}`;
    }
    if (col === 'Spread Type') return String(row['Spread Type'] || '').replace(' Spread', '');
    const value = row[col];
    if (value === null || value === undefined || value === '') return '-';
    if (value === 'NA') return 'NA';
    if (CURRENCY_COLUMNS.has(col)) return formatCurrency(value);
    if (PERCENT_COLUMNS.has(col)) {
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      if (Math.abs(num) >= 99999) return `${num.toExponential(1)}%`;
      if (Math.abs(num) >= 1000) return `${num.toFixed(0)}%`;
      return `${num.toFixed(2)}%`;
    }
    if (typeof value === 'number') return Number(value).toLocaleString('en-US');
    return value;
  };

  const isPositiveMetric = (col) => (
    col === 'Return on Risk %' || col === 'Spread Value Gain %' || col === 'Simulated P&L'
    || col === 'Ann. Return (Target)' || col === 'Ann. Return (Expiry)'
  );

  const csvData = useMemo(() => sortedData.map((row) => {
    const out = {};
    allColumns.forEach((col) => {
      out[col] = col === 'Strikes'
        ? formatStrikePair(row['Long Strike'], row['Short Strike'])
        : row[col];
    });
    return out;
  }), [sortedData, allColumns]);

  if (!Array.isArray(results) || results.length === 0) return null;

  return (
    <div className="mt-6">
      {/* The panel owns its own slider state and reports both values through a
          single debounced onRefresh, so we only mirror them into local state.
          The wrapper div is the tour anchor — the shared panel stays tour-agnostic. */}
      <div data-tour="spread-sim-settings">
        <SimulationSettingsPanel
          maxDays={maxDays}
          onRefresh={(days, gain) => {
            setDaysToTarget(days);
            setUnderlyingGain(gain);
            setSimulationActive(true);
          }}
          onReset={() => {
            setSimulationActive(false);
            setDaysToTarget(initialDays);
            setUnderlyingGain(0);
          }}
          isActive={simulationActive}
          defaultDays={initialDays}
          defaultGain={0}
          contractCount={sortedData.length}
          defaultExpanded={false}
        />
      </div>

      {/* Column group tabs */}
      <div className="flex flex-wrap gap-2 items-center justify-between mb-3">
        <div className="flex flex-wrap gap-2" data-tour="spread-column-tabs">
          {Object.keys(COLUMN_GROUPS).map((group) => (
            <button
              key={group}
              type="button"
              onClick={() => setSelectedGroup(group)}
              data-tour-group={group}
              className={`tab-button ${selectedGroup === group ? 'tab-selected' : 'tab-unselected'}`}
            >
              {group}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => setShowFavoritesOnly((p) => !p)}
            disabled={favoritedRows.size === 0 && !showFavoritesOnly}
            className={`flex items-center gap-1.5 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border ${
              showFavoritesOnly
                ? 'bg-yellow-400/20 text-yellow-400 border-yellow-400'
                : favoritedRows.size === 0
                  ? 'bg-[#1f2937] text-[#555] border-[#333] cursor-not-allowed'
                  : 'bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-yellow-400/10 hover:text-yellow-400 hover:border-yellow-400'
            }`}
          >
            <Star className="w-3.5 h-3.5" fill={showFavoritesOnly || favoritedRows.size > 0 ? 'currentColor' : 'none'} />
            Favorites{favoritedRows.size > 0 ? ` (${favoritedRows.size})` : ''}
          </button>
          <CSVLink
            data={csvData}
            filename="spread_detail_optionsLeveling.csv"
            className="btn-export-primary"
          >
            Export CSV
          </CSVLink>
        </div>
      </div>

      <div className="table-container">
        <div className="table-scroll-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell-sticky table-header-cell-sticky-0" />
                {allColumns.map((col, i) => {
                  const stickyIndex = STICKY_COLUMNS.indexOf(col);
                  const tip = COLUMN_TOOLTIPS[col];
                  return (
                    <th
                      key={col}
                      onClick={() => handleSort(col)}
                      className={stickyIndex >= 0
                        ? `table-header-cell-sticky table-header-cell-sticky-${stickyIndex + 1}`
                        : 'table-header-cell'}
                      {...(tip ? {
                        'data-tooltip-id': 'spread-detail-tooltip',
                        'data-tooltip-content': tip,
                      } : {})}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <span className="text-center">{col}</span>
                        <span className="mt-1">{getSortIcon(col)}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => {
                const key = rowKey(row);
                const isFavorited = favoritedRows.has(key);
                return (
                  <tr
                    key={key}
                    className={`${idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}${isFavorited ? ' table-row-favorited' : ''}`}
                  >
                    <td className="table-cell-sticky table-cell-sticky-0" style={{ padding: '2px' }}>
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => toggleFavorite(row)}
                          className="w-4 h-4 rounded-full border border-gray-600 hover:border-yellow-400 hover:bg-yellow-400/20 flex items-center justify-center transition-all duration-200 group"
                          title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Star
                            className={`w-3 h-3 ${isFavorited ? 'text-yellow-400' : 'text-gray-400 group-hover:text-yellow-400'}`}
                            fill={isFavorited ? 'currentColor' : 'none'}
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRow(row)}
                          className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#ef4444] hover:bg-[#ef4444]/20 flex items-center justify-center transition-all duration-200 group"
                          title="Remove from results"
                        >
                          <MinusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#ef4444]" />
                        </button>
                      </div>
                    </td>
                    {allColumns.map((col) => {
                      const stickyIndex = STICKY_COLUMNS.indexOf(col);
                      const numeric = parseFloat(row[col]);
                      let colorClass = '';
                      if (isPositiveMetric(col) && !isNaN(numeric)) {
                        colorClass = numeric > 0
                          ? 'table-cell-positive'
                          : (numeric < 0 ? 'table-cell-negative' : '');
                      }
                      return (
                        <td
                          key={col}
                          className={`${stickyIndex >= 0 ? `table-cell-sticky table-cell-sticky-${stickyIndex + 1}` : 'table-cell'} table-cell-number ${colorClass}`}
                          style={{
                            ...(col === 'Max Profit' ? { color: '#1DB954' } : {}),
                            ...(col === 'Max Loss' ? { color: '#ef4444' } : {}),
                          }}
                        >
                          {formatValue(row, col)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-sm text-gray-400 mt-3">
        Showing {sortedData.length} of {results.length} spreads
      </div>

      <ReactTooltip
        id="spread-detail-tooltip"
        place="top"
        delayShow={300}
        style={{
          backgroundColor: '#111827', color: '#fff', borderRadius: 8, padding: '8px 12px',
          fontSize: 13, maxWidth: 280, lineHeight: 1.4, zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)', border: '1px solid #374151',
        }}
      />
    </div>
  );
}

export default SpreadDetailTable;
